<template>
  <div>
    <hr />
    <div class="col-md-6">
      <div class="form-group" :class="{ error: hasError('company') }">
        <label :for="`${addressType}_company`">Organization Name:</label>
        <input
          :id="`${addressType}_company`"
          v-model="address.company"
          type="text"
          :name="`${addressType}_company`"
          class="form-control"
          maxlength="255"
        />
        <div v-if="hasError('company')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('firstName') }">
        <label :for="`${addressType}_first_name`">First Name:</label>
        <input
          :id="`${addressType}_first_name`"
          v-model="address.firstName"
          type="text"
          :name="`${addressType}_first_name`"
          class="form-control"
          maxlength="255"
        />
        <div v-if="hasError('firstName')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('lastName') }">
        <label :for="`${addressType}_last_name`">Last Name:</label>
        <input
          :id="`${addressType}_last_name`"
          v-model="address.lastName"
          type="text"
          :name="`${addressType}_last_name`"
          class="form-control"
          maxlength="255"
        />
        <div v-if="hasError('lastName')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('address') }">
        <label :for="`${addressType}_address`">Address:</label>
        <input
          :id="`${addressType}_address`"
          v-model="address.address"
          type="text"
          :name="`${addressType}_address`"
          class="form-control"
          maxlength="255"
        />
        <div v-if="hasError('address')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('address2') }">
        <label :for="`${addressType}_address2`">Suite, Apt, etc.:</label>
        <input
          :id="`${addressType}_address2`"
          v-model="address.address2"
          type="text"
          :name="`${addressType}_address2`"
          class="form-control"
          maxlength="255"
        />
      </div>
      <div v-if="hasError('address2')" class="invalid-feedback">
        <small>Required</small>
      </div>
    </div>
    <div class="row-md-6 col-md-6">
      <div class="form-group" :class="{ error: hasError('city') }">
        <label :for="`${addressType}_city`">City:</label>
        <input
          :id="`${addressType}_city`"
          v-model="address.city"
          type="text"
          :name="`${addressType}_city`"
          class="form-control"
          maxlength="255"
        />
        <div v-if="hasError('city')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('state') }">
        <label :for="`${addressType}_state`"> {{ regionLabel }}: </label>
        <select
          :id="`${addressType}_state`"
          v-model="address.state"
          :name="`${addressType}_state`"
          class="form-control"
        >
          <option :value="null">Please select</option>
          <option
            v-for="(region, regionAbbreviation) in regions"
            :key="regionAbbreviation"
            :value="regionAbbreviation"
          >
            {{ region }}
          </option>
        </select>
        <div v-if="hasError('state')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('zip') }">
        <label :for="`${addressType}_zip`">Zip / Postal Code:</label>
        <input
          :id="`${addressType}_zip`"
          v-model="address.zip"
          type="text"
          :name="`${addressType}_zip`"
          class="form-control"
          maxlength="255"
        />
        <div
          v-if="hasError('zip') || hasError('zip-invalid')"
          class="invalid-feedback"
        >
          <small v-if="hasError('zip-invalid')"> Invalid value </small>
          <small v-else> Required </small>
        </div>
      </div>
      <div class="form-group" :class="{ error: hasError('country') }">
        <label :for="`${addressType}_country`">Country:</label>
        <select
          :id="`${addressType}_country`"
          v-model="address.country"
          :name="`${addressType}_country`"
          class="form-control"
        >
          <option
            v-for="(countryName, countryAbbreviation) in countries"
            :key="countryAbbreviation"
            :value="countryAbbreviation"
          >
            {{ countryName }}
          </option>
        </select>
        <div v-if="hasError('country')" class="invalid-feedback">
          <small>Required</small>
        </div>
      </div>
      <div class="validation-section">
        <validate-address
          :address="address"
          :address-type="addressType"
          @missing-fields="setMissingFields"
          @validated="saveNewAddress()"
        ></validate-address>
      </div>
      <inline-loading-indicator v-if="submitting"></inline-loading-indicator>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert2";

export default {
  props: {
    addressType: {
      type: String,
      validator(value) {
        return ["billing", "shipping"].indexOf(value) !== -1;
      },
      required: true,
    },
    shipToBillingAddress: {
      type: Boolean,
      required: true,
    },
    regionLabel: {
      type: String,
      required: true,
    },
    regions: {
      type: Object,
      required: true,
    },
    countries: {
      type: Object,
      required: true,
    },
    country: {
      type: String,
      required: true,
    },
    saveAddressRoute: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      address: {
        company: null,
        firstName: null,
        lastName: null,
        address: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        country: this.country,
        validationType: null,
        validatedAddress: null,
      },
      missingFields: [],
      submitting: false,
    };
  },

  computed: {
    validationPending() {
      return this.address.validationType === null;
    },

    addressToValidate() {
      const address = {
        firstName: this.address.firstName,
        lastName: this.address.lastName,
        address: this.address.address,
        address2: this.address.address2,
        city: this.address.city,
        state: this.address.state,
        zip: this.address.zip,
        country: this.address.country,
      };

      return address;
    },
  },

  methods: {
    setMissingFields(missingFields) {
      this.missingFields = missingFields;
    },

    hasError(field) {
      return this.missingFields.includes(field);
    },

    checkRequiredFields() {
      const missingFields = [
        "firstName",
        "lastName",
        "address",
        "city",
        "state",
        "zip",
        "country",
      ].filter((field) => !this.address[field]);

      this.setMissingFields(missingFields);

      return missingFields.length === 0;
    },

    clearAddressForm() {
      this.address.company = null;
      this.address.firstName = null;
      this.address.lastName = null;
      this.address.address = null;
      this.address.address2 = null;
      this.address.city = null;
      this.address.state = null;
      this.address.zip = null;
      this.address.validationType = null;
      this.address.validatedAddress = null;
    },

    saveNewAddress() {
      this.checkRequiredFields();

      if (this.missingFields.length > 0) {
        return;
      }

      const payload = {
        address_type: this.addressType,
        ship_to_billing: this.shipToBillingAddress,
        company: this.address.company,
        first_name: this.address.firstName,
        last_name: this.address.lastName,
        address: this.address.address,
        address2: this.address.address2,
        city: this.address.city,
        state: this.address.state,
        zip: this.address.zip,
        country: this.address.country,
        validation_type: this.address.validationType,
        validated_address: this.address.validatedAddress,
      };

      this.submitting = true;

      this.$http
        .post(this.saveAddressRoute, payload)
        .then((response) => {
          this.$emit("new-address-saved", {
            addressType: this.addressType,
            address: response.data,
          });
          this.clearAddressForm();
        })
        .catch((e) => {
          if (e.response.status === 422) {
            this.missingFields = Object.keys(e.response.data.errors);
            if (
              this.hasError("zip") &&
              e.response.data.errors.zip.includes("validation.postal_code")
            ) {
              // special handling for the postal code server side validation
              this.missingFields.push("zip-invalid");
            }
          } else {
            swal.fire({
              title: `Unable to save address. ${e}`,
              padding: "2.5em",
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.invalid-feedback small {
  color: red;
}
.validation-section {
  margin-top: 3.85rem;
}
</style>
